import type { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useOptionalAuth } from '@/providers/AuthProvider'

/**
 * The password electronic signature, as one reusable block.
 *
 * Shared by the transition confirm screen and the undo-start screen so the two
 * cannot drift — they previously shared a single input by accident of layout,
 * which is how the undo panel ended up telling the operator to "enter your
 * password above" long after the password field had been replaced by a name.
 * It is a password again, and now they say so from the same source.
 *
 * Deliberately not `components/signature/SignatureDialog`: that is a whole
 * Dialog root, and this renders INSIDE the step's existing dialog — nesting a
 * second Radix dialog fights over the focus trap.
 *
 * Whether the password is CORRECT is not knowable here: the server answers that
 * when the token is minted, and the caller surfaces the refusal. This block
 * only knows whether something has been typed.
 */
export function SignatureConfirmPanel({
  meaning,
  signerName,
  value,
  onChange,
  onSubmit,
  signerPicker,
  signingForOther = false,
}: {
  /** What this signature attests, e.g. "Ended by". Goes on the Part 11 record. */
  meaning: string
  /** The signer's display name — never their id or username. */
  signerName: string
  value: string
  onChange: (value: string) => void
  /** Enter in the field submits, so signing does not require a mouse. */
  onSubmit: () => void
  /** "Sign as" picker (z8nrz7dt3z), rendered above the password when offered. */
  signerPicker?: ReactNode
  /** The picked signer is not the signed-in user — they type their own password. */
  signingForOther?: boolean
}) {
  // An SSO-only signer signs with their separate signing password.
  const signingPassword = useOptionalAuth()?.user?.ssoOnly === true && !signingForOther
  return (
    <div className='rounded-lg border border-gray-200 bg-white px-4 py-3'>
      <div className='flex items-center gap-2.5'>
        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600'>
          <Lock className='h-4 w-4' />
        </span>
        <div className='min-w-0'>
          <p className='text-sm font-semibold text-gray-900'>Electronic Signature</p>
          <p className='truncate text-[11px] text-gray-500'>
            Signing meaning: {meaning} · {signerName || 'not signed in'}
          </p>
        </div>
      </div>

      {signerPicker && <div className='mt-3'>{signerPicker}</div>}

      <form
        className='mt-3'
        onSubmit={e => {
          e.preventDefault()
          onSubmit()
        }}
      >
        {/* Deliberately hostile to password managers.
         *
         * With autoComplete='current-password' and a hidden username input,
         * Chrome fills this. That silently replaces the signer's password with
         * whatever credential the browser had stored, and the only feedback is
         * "Password could not be verified" on a field that visibly contains a
         * password.
         *
         * It is also wrong on the regulation's own terms: a 21 CFR Part 11
         * signature must be executed BY the signer AT the moment of signing. A
         * credential replayed from a browser store is neither, and it is
         * exactly the "someone left the session open" case that re-entry exists
         * to catch. */}
        <Input
          type='password'
          name='signature-password'
          autoComplete='off'
          data-1p-ignore
          data-lpignore='true'
          data-form-type='other'
          autoFocus
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={
            signingForOther
              ? `${signerName}'s own password`
              : signingPassword
                ? 'Enter your signing password'
                : 'Enter your password'
          }
          aria-label='Enter your password to sign'
          className='h-9 w-full'
        />
      </form>

      <p className='mt-1.5 text-[11px] text-gray-400'>
        {signingForOther
          ? `${signerName} enters their own password — the signature is recorded as theirs`
          : signingPassword
            ? 'Enter your signing password to sign'
            : 'Enter your account password to sign'}
      </p>
      {signingPassword && (
        // New tab, so the step being signed keeps its state.
        <a
          href='/profile#signing-password'
          target='_blank'
          rel='noreferrer'
          className='text-[11px] text-blue-600 hover:underline'
        >
          Set or reset signing password
        </a>
      )}
    </div>
  )
}
